import classNames from "classnames"
import type Swiper from "swiper"
import { Navigation, Thumbs } from "swiper/modules"
import { Swiper as SwiperComponent, SwiperSlide } from "swiper/react"

import GalleryNavButton from "./GalletNavButton"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import "./styles.css"
import Video from "../../../experiments/Video"

import Badge from "@components/elements/V2/Badge"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import React, { useRef, useState } from "react"

export type GalleryProps = {
  galleryItems: Storyblok.ProductGallery[]
  badge?: YocoCom.Badge
  onOpen?: (index: number) => void
  hasLoop?: boolean
}

const GALLERY_MAIN_SIZES =
  "(min-width: 1200px) 40vw, (min-width: 900px) 45vw, (min-width: 667px) calc(100vw - 80px), calc(100vw - 40px)"

const GALLERY_SRCSET_WIDTHS = [480, 640, 768, 1024, 1200, 1600, 2048]

export default function ProductInfoSectionGallery({
  galleryItems,
  badge,
  onOpen,
  hasLoop,
}: GalleryProps) {
  const [thumbsSwiper, setThumbsSwiper] = useState<Swiper>()
  const [activeIndex, setActiveIndex] = useState(0)
  const [navigationState, setNavigationState] = useState({
    isBeginning: true,
    isEnd: false,
  })

  const swiperReference = useRef<Swiper | null>(null)

  const handleSlideChange = (swiper: Swiper) => {
    setActiveIndex(swiper.realIndex)
    if (!hasLoop) {
      setNavigationState({
        isBeginning: swiper.isBeginning,
        isEnd: swiper.isEnd,
      })
    }
  }

  const handlePrev = () => swiperReference.current?.slidePrev()
  const handleNext = () => swiperReference.current?.slideNext()

  return (
    <>
      {badge && (
        <Badge
          text={badge.text}
          textColor={badge.textColor || Color.White}
          accentColor={badge.color}
          style="solid"
          className="absolute left-32 top-48 z-10 !rounded-20 sm:left-56 sm:top-48 md:left-20 md:top-60"
          uppercaseText={false}
        />
      )}

      <div className="group relative w-full">
        <SwiperComponent
          modules={[Navigation, Thumbs]}
          slidesPerView="auto"
          slidesOffsetBefore={20}
          slidesOffsetAfter={20}
          spaceBetween={40}
          onSwiper={(swiper: Swiper) => (swiperReference.current = swiper)}
          onSlideChange={handleSlideChange}
          onResize={(swiper) => swiper.update()}
          onBreakpoint={(swiper) => setActiveIndex(swiper.realIndex)}
          breakpoints={{
            667: {
              spaceBetween: 50,
              slidesOffsetBefore: 40,
              slidesOffsetAfter: 40,
            },
            900: {
              height: 600,
              slidesPerView: 1,
              spaceBetween: 10,
              slidesOffsetBefore: 0,
              slidesOffsetAfter: 0,
            },
          }}
          thumbs={{ swiper: thumbsSwiper }}
          grabCursor
          loop={hasLoop && galleryItems.length > 1}
          enabled={galleryItems.length > 1}
        >
          {galleryItems.map((galleryItem, index) => (
            <SwiperSlide
              key={`${galleryItem.image.filename}-${index}`}
              className="!flex h-auto !w-[calc(100%-40px)] items-center justify-center rounded-16 bg-light-gray sm:!w-[calc(100%-80px)] md:!w-full"
              onClick={() => onOpen?.(index)}
            >
              {galleryItem.imageLabel?.[0]?.icon?.filename &&
                galleryItem.imageLabel[0]?.text && (
                  <div className="absolute bottom-20 left-20 z-10 flex max-w-[300px] items-center gap-8 rounded-[6px] bg-white/70 px-10 py-4 sm:bottom-24 sm:left-24 sm:max-w-none">
                    <GatsbyStoryblokImage
                      image={galleryItem.imageLabel[0].icon.filename}
                      alt={galleryItem.imageLabel[0].icon.alt}
                      height={24}
                      width={24}
                      aspectRatio="1:1"
                      className="hidden sm:block"
                    />
                    <GatsbyStoryblokImage
                      image={galleryItem.imageLabel[0].icon.filename}
                      alt={galleryItem.imageLabel[0].icon.alt}
                      height={16}
                      width={16}
                      aspectRatio="1:1"
                      className="sm:hidden"
                    />
                    <Typography
                      text={galleryItem.imageLabel[0].text}
                      size="subscript-lg"
                      font="grotesk"
                      weight="medium"
                      color={Color.Charcoal}
                      className="pt-[2px]"
                    />
                  </div>
                )}
              <div className={"relative w-full cursor-zoom-in"}>
                {galleryItem.video?.filename ? (
                  <Video
                    video={galleryItem.video.filename}
                    coverImage={galleryItem.image.filename}
                    ImageAlt={"image"}
                    loadType="eager"
                    aspectRatio="4:3"
                    playbackMode="autoplay"
                    rounded
                    playOnMobile={false}
                    sizes={GALLERY_MAIN_SIZES}
                    srcSetWidths={GALLERY_SRCSET_WIDTHS}
                    fetchPriority={index === 0 ? "high" : undefined}
                  />
                ) : (
                  <GatsbyStoryblokImage
                    image={galleryItem.image.filename}
                    alt={galleryItem.image.alt || "Product thumbnail"}
                    aspectRatio="4:3"
                    quality={80}
                    rounded
                    className="w-full"
                    testId="gallery-slide-image"
                    sizes={GALLERY_MAIN_SIZES}
                    srcSetWidths={GALLERY_SRCSET_WIDTHS}
                    loadType={index === 0 ? "eager" : "lazy"}
                    fetchPriority={index === 0 ? "high" : undefined}
                  />
                )}
              </div>
            </SwiperSlide>
          ))}
        </SwiperComponent>

        {galleryItems.length > 1 && (
          <>
            <GalleryNavButton
              onClick={handlePrev}
              direction="left"
              className={classNames(
                "absolute left-20 top-1/2 z-10 hidden -translate-y-1/2 sm:flex md:hidden md:group-hover:flex",
                { "!opacity-0": !hasLoop && navigationState.isBeginning }
              )}
            />

            <GalleryNavButton
              onClick={handleNext}
              direction="right"
              className={classNames(
                "absolute right-20 top-1/2 z-10 hidden -translate-y-1/2 sm:flex md:hidden md:group-hover:flex",
                { "!opacity-0": !hasLoop && navigationState.isEnd }
              )}
            />
          </>
        )}
      </div>

      {galleryItems.length > 1 && (
        <div className="mt-16 flex justify-center gap-24 sm:hidden">
          {galleryItems.map((_, dotIndex) => (
            <button
              key={dotIndex}
              aria-label={`Open slide ${activeIndex + 1}`}
              onClick={() => onOpen?.(activeIndex)}
              className={classNames(
                "h-2 w-2 rounded-full bg-charcoal transition-opacity duration-150",
                dotIndex === activeIndex ? "opacity-100" : "opacity-20"
              )}
            />
          ))}
        </div>
      )}

      {galleryItems.length > 1 && (
        <div className="hidden sm:block">
          <SwiperComponent
            className={classNames(
              "thumbs-swiper mt-20 w-full transition-opacity duration-150",
              {
                "opacity-0": !thumbsSwiper,
              }
            )}
            onSwiper={(swiper) => setThumbsSwiper(swiper)}
            onResize={(swiper) => swiper.update()}
            observer={true}
            observeParents={true}
            spaceBetween={8}
            centerInsufficientSlides
            slidesOffsetBefore={20}
            slidesOffsetAfter={20}
            watchSlidesProgress={true}
            slidesPerView={galleryItems.length <= 5 ? 5 : 5.55}
            breakpoints={{
              667: {
                slidesOffsetBefore: 40,
                slidesOffsetAfter: 30,
              },
              900: {
                slidesOffsetBefore: 0,
              },
              1200: {
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 0,
              },
            }}
          >
            {galleryItems.map((galleryItem, index) => (
              <SwiperSlide
                key={`thumb-${galleryItem.image.filename}-${index}`}
                className={classNames(
                  "!flex cursor-pointer items-center justify-center overflow-hidden rounded-8 border-[1.5px] bg-light-gray sm:border-[2px]",
                  index === activeIndex ? "border-blue" : "border-transparent"
                )}
              >
                {galleryItem.video?.filename && (
                  <img
                    src="/icons/V2/play-icon.svg"
                    alt="play icon"
                    height={30}
                    width={30}
                    className="absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 sm:h-[38px] sm:w-[38px]"
                  />
                )}
                <GatsbyStoryblokImage
                  image={galleryItem.image.filename}
                  alt={galleryItem.image.alt || "Product thumbnail"}
                  aspectRatio="4:3"
                  testId="gallery-slide-thumbnail-image"
                  sizes="(min-width: 900px) 96px, 20vw"
                />
              </SwiperSlide>
            ))}
            <div className="absolute right-0 top-0 z-10 -mr-2 h-full w-1/12 bg-gradient-to-l from-white from-15% to-transparent"></div>
          </SwiperComponent>
        </div>
      )}
    </>
  )
}
